import {
    describeBrokenQueries,
    describeChangedReadings,
    queriesBrokenBy,
    readingsChangedBy,
} from '@/app/considerations/appliesToPublishCheck';
import { considerationTypesState, hqlCatalogState, lessonTypesState } from '@/state';
import { useLoadableAtomValue } from '@/state/loadableHooks';
import type { HqlCatalog } from '@rock/hql';
import { useStore } from 'jotai';
import { toast } from 'sonner';

/**
 * A check to run before any catalog write that could invalidate an approved applies-to query (a
 * publish, a delete, an info-type edit, unpublishing a space). It throws, naming every query the change
 * would break, so the write never happens and the caller's error handling shows why. A change that only
 * turns some stored answers unknown to a query (an option value removed) goes ahead with a warning.
 */
export function useAppliesToGuard() {
    const store = useStore();
    // Subscribed while the page is open, so the check reads live data rather than a stale cached copy.
    useLoadableAtomValue(hqlCatalogState);
    useLoadableAtomValue(considerationTypesState);
    useLoadableAtomValue(lessonTypesState);
    return async (change: string, propose: (catalog: HqlCatalog) => HqlCatalog): Promise<void> => {
        const [catalog, considerationTypes, lessonTypes] = await Promise.all([
            store.get(hqlCatalogState),
            store.get(considerationTypesState),
            store.get(lessonTypesState),
        ]);
        const types = [...considerationTypes, ...lessonTypes];
        const proposed = propose(catalog);
        const broken = queriesBrokenBy(types, catalog, proposed);
        if (broken.length > 0) throw new Error(describeBrokenQueries(change, broken));
        const changed = readingsChangedBy(types, catalog, proposed);
        if (changed.length > 0) toast.warning(describeChangedReadings(change, changed));
    };
}
